import React from 'react';
import {
	Box,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Image,
	Flex,
	Heading,
	Text,
	Icon,
	Button,
	Badge,
	Stack,
} from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';
import { BiCreditCard } from 'react-icons/bi';
import { FaShippingFast } from 'react-icons/fa';
import moment from 'moment';
import { OrderModel } from 'models';
import { OrderPaymentStatus, OrderStatus } from 'models/Order/types';
import { toDollarFormat } from 'utils/format';
import OrderTableSkeleton from '../OrderTableSkeleton/OrderTableSkeleton';

export interface BuyerOrdersTableProps {
	orders: OrderModel[];
	onOrderNumberClick?: (orderId: string) => void;
	onItemNameClick?: (itemId: string, itemName: string) => void;
	onPayNowClick?: (order: OrderModel) => void;
	isLoading?: boolean
}

export default function BuyerOrdersTable({
	orders,
	onOrderNumberClick,
	onItemNameClick,
	onPayNowClick,
	isLoading = false
}: BuyerOrdersTableProps) {
	const getOrderStatusColor = (status: OrderStatus): string => {
		switch (status) {
			case OrderStatus.UNCONFIRMED:
				return 'orange';
			case OrderStatus.CONFIRMED:
			case OrderStatus.IN_TRANSIT:
				return 'blue';
			case OrderStatus.COMPLETE:
				return 'green';
			case OrderStatus.CANCELLED:
				return 'red';
			default:
				return 'gray';
		}
	};

	const getPaymentStatusColor = (status: OrderPaymentStatus): string => {
		switch (status) {
			case OrderPaymentStatus.PENDING:
				return 'orange';
			case OrderPaymentStatus.COMPLETE:
				return 'green';
			case OrderPaymentStatus.FAILED:
				return 'red';
			default:
				return 'gray';
		}
	};

	function renderItemInfo(order: OrderModel) {
		const item = order.item;
		if (!item) return null;
		return (
			<Flex alignItems="center">
				<Box minWidth="50px" maxWidth="50px" mr={4}>
					<Image src={item.image} alt={item.name} boxSize="50px" objectFit="contain" />
				</Box>
				<Box>
				<Heading
						fontSize="sm"
						onClick={() => onOrderNumberClick?.(order.id)}
						_hover={onOrderNumberClick ? { color: 'blue.500', cursor:'pointer'} : {}}
					>
						Order #{order.orderNumber}
					</Heading>
					<Text
						fontSize="xs"
						onClick={() => onItemNameClick?.(item.id, item.getDisplayName())}
						_hover={onItemNameClick ? { color: 'blue.500', cursor: 'pointer'} : {}}
					>
						{item.getDisplayName()}
					</Text>
				</Box>
			</Flex>
		);
	}

	function renderIcons(order: OrderModel) {
		const paymentColor = getPaymentStatusColor(order.paymentStatus);
		let shippingColor = 'gray';
		if (order.timestamps.shippedAt) {
			shippingColor = 'blue';
		}
		if (order.timestamps.deliveredAt) {
			shippingColor = 'green';
		}
		return (
			<Flex gap={2}>
				<Icon as={BiCreditCard} w={4} h={4} color={paymentColor} />
				<Icon as={FaShippingFast} w={4} h={4} color={shippingColor} />
			</Flex>
		);
	}

	function renderAction(order: OrderModel) {
		if (order.paymentStatus === OrderPaymentStatus.PENDING && order.paymentLink) {
			return (
				<Button
					size="xs"
					colorScheme="red"
					leftIcon={<WarningIcon />}
					onClick={() => onPayNowClick?.(order)}
				>
					Pay Now
				</Button>
			);
		}
		return null;
	}

	if (isLoading) {
		return <OrderTableSkeleton rows={4} />;
	}

	else if (orders.length === 0) {
		return <Box>No Orders Found</Box>;
	}

	return (
		<Box overflowX="auto">
			<Table variant="simple" size="sm">
				<Thead>
					<Tr>
						<Th>Item</Th>
						<Th>Order Total</Th>
						<Th>Date</Th>
						<Th>Status</Th>
						<Th>Tracking</Th>
						<Th>Action</Th>
					</Tr>
				</Thead>
				<Tbody>
					{orders.map((order) => (
						<Tr key={order.id}>
							<Td>{renderItemInfo(order)}</Td>
							<Td fontSize="xs">{toDollarFormat(order.amounts.grossTotal)}</Td>
							<Td fontSize="xs">
								{order.timestamps.createdAt ? moment(order.timestamps.createdAt).format('MM/DD/YYYY') : '-'}
							</Td>
							<Td>
								<Stack>
									<Badge colorScheme={getOrderStatusColor(order.orderStatus)}>
										{order.orderStatus}
									</Badge>
									{renderIcons(order)}
								</Stack>
							</Td>
							<Td fontSize="xs ">
								{order.trackingInfo?.number ? order.trackingInfo?.number : '-'}
							</Td>
							<Td>{renderAction(order)}</Td>
						</Tr>
					))}
				</Tbody>
			</Table>
		</Box>
	);
}
